import { useCallback, useEffect, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getEventCategories } from './events';
import type { EventFacetDto } from '@/services/platform/api/model';

export interface UseEventCategoriesResult {
  categories: EventFacetDto[];
  isLoading: boolean;
  hasFetched: boolean;
  refetch: () => Promise<void>;
}

/**
 * Fetches calendar-category facets (the filter pills), scoped to a hub — and to `visibility` when
 * the "Members only" filter is on, so a pill never counts events the list has filtered out. Counts
 * here reflect the hub's whole upcoming set; per-selection re-scoping against selected dates is
 * computed client-side (see `facets.ts`).
 */
export function useEventCategories(hubKey?: string, visibility?: string[]): UseEventCategoriesResult {
  const locale = useLocaleStore(state => state.locale);
  const [categories, setCategories] = useState<EventFacetDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const visibilityKey = visibility?.join(',') ?? '';

  const refetch = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getEventCategories(
        {
          hubKey: hubKey || undefined,
          includeRestricted: true,
          visibility: visibilityKey ? visibilityKey.split(',') : undefined,
        },
        locale
      );
      setCategories(result);
    } finally {
      setIsLoading(false);
      setHasFetched(true);
    }
  }, [hubKey, visibilityKey, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { categories, isLoading, hasFetched, refetch };
}
